<template>
    <el-dialog title="New Customer"
               width="50%"
               :visible="showForm"
               :show-close="!isBusy"
               @close="$emit('is-visible', false)"
               :close-on-click-modal="!isBusy"
               :close-on-press-escape="!isBusy">
        <el-alert v-if="newCustomerFormErrors.length > 0" type="error" title="Form contains errors:" :closable="false">
            <ul>
                <li v-for="(error, index) of newCustomerFormErrors" :key="`error_li_${index}`">{{error}}</li>
            </ul>
        </el-alert>
        <el-form :model="newCustomer" ref="newCustomerForm" :rules="newCustomerFormRules">
            <el-form-item label="ID" prop="id">
                <el-input type="text" v-model="newCustomer.id"/>
            </el-form-item>
            <div class="mb-10">ID represents employer in Braintree Vault, must be unique with max 36 characters. Valid
                characters are letters, numbers, dash and underscore. The words "<b>all</b>" and "<b>new</b>" can't be
                used currently in any form.
            </div>

            <el-form-item label="Company" prop="company" class="mb-10" required>
                <el-input type="text" v-model="newCustomer.company"/>
            </el-form-item>
            <el-row>
                <el-col :span="12" class="pr-5">
                    <el-form-item label="First Name" prop="firstName" class="mb-5">
                        <el-input type="text" v-model="newCustomer.firstName"/>
                    </el-form-item>
                </el-col>
                <el-col :span="12" class="pr-5">
                    <el-form-item label="Last Name" prop="lastName" class="mb-5">
                        <el-input type="text" v-model="newCustomer.lastName"/>
                    </el-form-item>
                </el-col>
            </el-row>
            <el-row>
                <el-col :span="12" class="pr-5">
                    <el-form-item label="Email" prop="email" class="mb-10" required>
                        <el-input type="text" v-model="newCustomer.email"/>
                    </el-form-item>
                </el-col>
                <el-col :span="12" class="pr-5">
                    <el-form-item label="Website" prop="website" class="mb-10">
                        <el-input type="text" v-model="newCustomer.website"/>
                    </el-form-item>
                </el-col>
            </el-row>
            <el-row>
                <el-col :span="12" class="pr-5">
                    <el-form-item label="Phone" prop="phone" class="mb-5">
                        <el-input type="text" v-model="newCustomer.phone"/>
                    </el-form-item>
                </el-col>
                <el-col :span="12" class="pr-5">
                    <el-form-item label="Fax" prop="fax" class="mb-5">
                        <el-input type="text" v-model="newCustomer.fax"/>
                    </el-form-item>
                </el-col>
            </el-row>
        </el-form>
        <span slot="footer" class="dialog-footer">
                    <el-button @click="$emit('is-visible', false)" :disabled="isBusy">Cancel</el-button>
                    <el-button type="primary" @click="validateCustomer" :loading="isBusy">Create</el-button>
                </span>
    </el-dialog>
</template>

<script>
    const stringMaxLength = 255;
    const incorrectLengthMsg = 'Length should be below ' + stringMaxLength + ' characters';

    export default {
        props: {
            showForm: { type: Boolean, required: true },
            employerId: { type: Number, required: true },
            employerName: { type: String, required: true },
            employerEmail: { type: String, required: true },
        },
        data() {
            const validateId = (rule, value, callback) => {
                if (value.trim() === '') {
                    callback();
                } else if (value.match(/new/i) || value.match(/all/i)) {
                    callback(new Error(`ID contains one of the keywords 'new' or 'all'.`));
                } else if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
                    callback(new Error(`Only allowed characters are letter, numbers, dash and underscore.`));
                }
                callback();
            };

            return {
                isBusy: false,
                newCustomer: {},
                newCustomerFormErrors: [],
                newCustomerFormRules: {
                    id: [
                        { max: 36, message: 'Length must be below 36 characters', trigger: 'blur' },//https://developers.braintreepayments.com/reference/request/customer/create/php#id
                        { validator: validateId, trigger: 'blur' },
                    ],
                    company: [
                        { required: true, message: 'Please input Company Name.', trigger: 'blur' },
                        { max: stringMaxLength, message: incorrectLengthMsg, trigger: 'blur' },
                    ],
                    email: [
                        { required: true, message: 'Please input Company e-mail address.', trigger: 'blur' },
                        { max: stringMaxLength, message: incorrectLengthMsg, trigger: 'blur' },
                    ],
                    firstName: [{ max: stringMaxLength, message: incorrectLengthMsg, trigger: 'blur' }],
                    lastName: [{ max: stringMaxLength, message: incorrectLengthMsg, trigger: 'blur' }],
                    phone: [{ max: stringMaxLength, message: incorrectLengthMsg, trigger: 'blur' }],
                    fax: [{ max: stringMaxLength, message: incorrectLengthMsg, trigger: 'blur' }],
                    website: [{ max: stringMaxLength, message: incorrectLengthMsg, trigger: 'blur' }],
                },
            };
        },
        computed: {
            emptyCustomer: function () {
                let id = this.employerName.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();
                while(id.includes('__')) id = id.replace('__', '_');
                while(id.includes('new')) id = id.replace(/new/ig, '');
                while(id.includes('all')) id = id.replace(/all/ig, '');

                return {
                    id: id,
                    company: this.employerName,
                    firstName: '',
                    lastName: '',
                    email: this.employerEmail,
                    phone: '',
                    fax: '',
                    website: '',
                };
            },
        },
        mounted() {
            this.newCustomer = this.emptyCustomer;
        },
        methods: {
            validateCustomer() {
                this.$refs['newCustomerForm'].validate((valid) => {
                    if (valid) {
                        this.createCustomer();
                    } else {
                        return false;
                    }
                });
            },
            createCustomer() {
                const $this = this;
                $this.isBusy = true;

                axios.post(url(`admin/employer/${$this.employerId}/braintree/create-customer`), $this.newCustomer)
                     .then(response => {
                         const data = response.data;
                         if (typeof data.id !== 'undefined' && typeof data.token !== 'undefined') {
                             $this.$emit('created', {
                                 customerId: data.id,
                                 customerToken: data.token,
                                 customerCompany: $this.newCustomer.company,
                                 customerEmail: $this.newCustomer.email,
                             });
                             $this.newCustomer = $this.emptyCustomer;
                             $this.$emit('is-visible', false);
                         } else if (typeof data.error !== 'undefined' && data.error === true) {
                             $this.newCustomerFormErrors = data.messages;
                         } else {
                             throw Error('Whoops, looks like something went wrong.');
                         }
                     })
                     .catch(err => this.$message.error(err.message))
                     .finally(() => {
                         $this.isBusy = false;
                     });
            },
        },
    };
</script>

<style scoped>
    .pr-5 { padding-right: 5px; }

    .mb-5 { margin-bottom: 5px; }

    .mb-10 { margin-bottom: 10px; }
</style>